import { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { useRiderStore } from '../../stores';

/* ============================================
   GRAPHQL QUERY DEFINITIONS
   ============================================ */

// Get all drivers.
const GET_ALL_DRIVERS = gql`
  query GetAllDrivers {
    getAllDrivers {
      id
      name
      carMake
      carModel
      carColor
      seats
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get single driver.
const GET_SINGLE_DRIVER = gql`
  query GetDriver($id: ID!) {
    getDriver(id: $id) {
      id
      email
      name
      phoneNumber
      licensePlate
      carMake
      carModel
      carColor
      seats
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
      acceptedRidesTotal
      canceledByRiderRidesTotal
      canceledByDriverRidesTotal
      completedRidesTotal
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get available drivers.
const GET_AVAILABLE_DRIVERS = gql`
  query GetAvailableDrivers {
    getAvailableDrivers {
      id
      name
      carMake
      carModel
      carColor
      seats
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get unavailable drivers.
const GET_UNAVAILABLE_DRIVERS = gql`
  query GetUnavailableDrivers {
    getUnavailableDrivers {
      id
      name
      carMake
      carModel
      carColor
      seats
      isOnDuty
      currentRideId
      riderQueue
      driverNextAvailableAt
      profilePicture
      createdAt
      updatedAt
    }
  }
`;

// Get a driver's availability.
const GET_DRIVER_NEXT_AVAILABLE_AT = gql`
  query GetDriverNextAvailableAt($id: ID!) {
    getDriverNextAvailableAt(id: $id) {
      driverNextAvailableAt
    }
  }
`;

// Get driver's completed rides.
const GET_DRIVER_COMPLETED_RIDES = gql`
  query GetDriverCompletedRides($id: ID!) {
    getDriverCompletedRides(id: $id) {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledAt
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

// Get driver's ride history.
const GET_DRIVER_RIDE_HISTORY = gql`
  query GetDriverRideHistory($id: ID!) {
    getDriverRideHistory(id: $id) {
      id
      riderId
      riderName
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      requestedDriverIds
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledAt
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

/* ============================================
   QUERY HOOKS
   ============================================ */

// Get all drivers
export const useAllDrivers = () => {
  // Get the rider store update method.
  const { setAllDrivers } = useRiderStore();

  const {
    data: allDriversData,
    loading: loadingAllDrivers,
    error: errorAllDrivers,
    refetch: refetchAllDrivers,
  } = useQuery(GET_ALL_DRIVERS);

  // Update rider store when data changes.
  useEffect(() => {
    if (allDriversData?.getAllDrivers) {
      setAllDrivers(allDriversData.getAllDrivers);
    }
  }, [allDriversData, setAllDrivers]);

  return {
    allDriversData,
    loadingAllDrivers,
    errorAllDrivers,
    refetchAllDrivers,
  };
};

// Get on duty drivers
export const useAvailableDrivers = () => {
  // Get the rider store update method.
  const { setAvailableDrivers } = useRiderStore();

  const {
    data: availableDriversData,
    loading: loadingAvailableDrivers,
    error: errorAvailableDrivers,
    refetch: refetchAvailableDrivers,
  } = useQuery(GET_AVAILABLE_DRIVERS);

  // Update rider store when data changes.
  useEffect(() => {
    if (availableDriversData?.getAvailableDrivers) {
      setAvailableDrivers(availableDriversData.getAvailableDrivers);
    }
  }, [availableDriversData, setAvailableDrivers]);

  return {
    availableDriversData,
    loadingAvailableDrivers,
    errorAvailableDrivers,
    refetchAvailableDrivers,
  };
};

// Get off duty drivers
export const useUnavailableDrivers = () => {
  // Get the rider store update method.
  const { setUnavailableDrivers } = useRiderStore();

  const {
    data: unavailableDriversData,
    loading: loadingUnavailableDrivers,
    error: errorUnavailableDrivers,
    refetch: refetchUnavailableDrivers,
  } = useQuery(GET_UNAVAILABLE_DRIVERS);

  // Update rider store when data changes.
  useEffect(() => {
    if (unavailableDriversData?.getUnavailableDrivers) {
      setUnavailableDrivers(unavailableDriversData.getUnavailableDrivers);
    }
  }, [unavailableDriversData, setUnavailableDrivers]);

  return {
    unavailableDriversData,
    loadingUnavailableDrivers,
    errorUnavailableDrivers,
    refetchUnavailableDrivers,
  };
};

// Get a single driver
export const useSingleDriver = (userId) => {
  const {
    data: driverData,
    loading: loadingDriver,
    error: errorDriver,
    refetch: refetchDriver,
  } = useQuery(GET_SINGLE_DRIVER, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return { driverData, loadingDriver, errorDriver, refetchDriver };
};

// Get driver next available at
export const useDriverNextAvailable = (userId) => {
  const {
    data: driverNextAvailableData,
    loading: loadingDriverNextAvailable,
    error: errorDriverNextAvailable,
    refetch: refetchDriverNextAvailable,
  } = useQuery(GET_DRIVER_NEXT_AVAILABLE_AT, {
    variables: { id: userId },
    skip: isEmpty(userId),
    fetchPolicy: 'no-cache',
  });

  return {
    driverNextAvailableData,
    loadingDriverNextAvailable,
    errorDriverNextAvailable,
    refetchDriverNextAvailable,
  };
};

// Get driver's completed rides
export const useDriverCompletedRides = (userId) => {
  const {
    data: completedDriverRidesData,
    loading: loadingDriverCompletedRides,
    error: errorDriverCompletedRides,
    refetch: refetchCompletedDriverRides,
  } = useQuery(GET_DRIVER_COMPLETED_RIDES, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return {
    completedDriverRidesData,
    loadingDriverCompletedRides,
    errorDriverCompletedRides,
    refetchCompletedDriverRides,
  };
};

// Get driver's ride history
export const useDriverRideHistory = (userId) => {
  const {
    data: driverRideHistoryData,
    loading: loadingDriverRideHistory,
    error: errorDriverRideHistory,
    refetch: refetchDriverRideHistory,
  } = useQuery(GET_DRIVER_RIDE_HISTORY, {
    variables: { id: userId },
    skip: isEmpty(userId),
  });

  return {
    driverRideHistoryData,
    loadingDriverRideHistory,
    errorDriverRideHistory,
    refetchDriverRideHistory,
  };
};